import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
  LinearProgress,
  InputAdornment,
  Avatar,
  Rating,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Business as VendorIcon,
  Person as ContactIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as AddressIcon,
  Assignment as ContractIcon,
  Security as InsuranceIcon,
  Star as RatingIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Check as ApprovalIcon,
  Close as RejectIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  AttachFile as DocumentIcon,
  Note as NoteIcon,
  Warning as WarningIcon,
  CheckCircle as ActiveIcon,
  CheckCircle,
  Schedule as PendingIcon,
  Block as SuspendedIcon,
  MonetizationOn as SpendingIcon,
  Assessment as PerformanceIcon,
  Category as CategoryIcon,
  DateRange as ExpirationIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  Upload as UploadIcon,
  Verified as VerifiedIcon,
  TrendingUp as TrendingIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import api from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => {
  const getColor = () => {
    switch (status) {
      case 'active': return { bg: '#4caf50', color: '#fff' };
      case 'pending_approval': return { bg: '#ff9800', color: '#fff' };
      case 'suspended': return { bg: '#f44336', color: '#fff' };
      case 'inactive': return { bg: '#9e9e9e', color: '#fff' };
      case 'blacklisted': return { bg: '#000000', color: '#fff' };
      default: return { bg: '#e0e0e0', color: '#000' };
    }
  };

  const colors = getColor();
  return {
    backgroundColor: colors.bg,
    color: colors.color,
  };
});

const CategoryChip = styled(Chip)<{ category: string }>(({ theme, category }) => {
  const getColor = () => {
    switch (category) {
      case 'maintenance_repair': return { bg: '#2196f3', color: '#fff' };
      case 'utilities': return { bg: '#ff9800', color: '#fff' };
      case 'professional_services': return { bg: '#9c27b0', color: '#fff' };
      case 'supplies': return { bg: '#4caf50', color: '#fff' };
      case 'emergency': return { bg: '#f44336', color: '#fff' };
      default: return { bg: '#607d8b', color: '#fff' };
    }
  };

  const colors = getColor();
  return {
    backgroundColor: colors.bg,
    color: colors.color,
  };
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vendor-tabpanel-${index}`}
      aria-labelledby={`vendor-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function VendorPage() {
  const [currentTab, setCurrentTab] = useState(0);
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    vendorType: 'all',
    search: ''
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalVendors, setTotalVendors] = useState(0);

  // Dashboard stats
  const [vendorStats, setVendorStats] = useState({
    totalVendors: 0,
    activeVendors: 0,
    pendingApproval: 0,
    totalSpent: 0,
    averageRating: 0,
    expiringDocuments: 0,
    topPerformers: [] as any[]
  });

  // Additional state for different tabs
  const [pendingVendors, setPendingVendors] = useState<any[]>([]);
  const [topPerformers, setTopPerformers] = useState<any[]>([]);

  useEffect(() => {
    fetchVendorData();
  }, [currentTab, filters, page, rowsPerPage]);

  const fetchVendorData = async () => {
    setLoading(true);
    try {
      switch (currentTab) {
        case 0: // Dashboard
          await fetchVendorStats();
          break;
        case 1: // All Vendors
          await fetchVendors();
          break;
        case 2: // Pending Approval
          await fetchPendingVendors();
          break;
        case 3: // Performance
          await fetchTopPerformers();
          break;
      }
    } catch (error) {
      console.error('Error fetching vendor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorStats = async () => {
    try {
      const response = await api.get('/vendors/analytics/statistics');
      if (response.data.success) {
        setVendorStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching vendor stats:', error);
      // Fallback to default values on error
      setVendorStats({
        totalVendors: 0,
        activeVendors: 0,
        pendingApproval: 0,
        totalSpent: 0,
        averageRating: 0,
        expiringDocuments: 0,
        topPerformers: []
      });
    }
  };

  const fetchVendors = async () => {
    try {
      const params = new URLSearchParams({
        ...filters,
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      });

      const response = await api.get(`/vendors?${params}`);
      
      if (response.data.success) {
        setVendors(response.data.data || []);
        setTotalVendors(response.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setVendors([]);
      setTotalVendors(0);
    }
  };

  const fetchPendingVendors = async () => {
    try {
      const response = await api.get('/vendors?status=pending_approval');
      
      if (response.data.success) {
        const pending = response.data.data || [];
        setPendingVendors(pending);
        setVendors(pending);
        setTotalVendors(pending.length);
      }
    } catch (error) {
      console.error('Error fetching pending vendors:', error);
      setPendingVendors([]);
      setVendors([]);
      setTotalVendors(0);
    }
  };

  const fetchTopPerformers = async () => {
    try {
      const response = await api.get('/vendors/analytics/performance-trends');
      
      if (response.data.success) {
        const performers = response.data.data.topPerformers || [];
        setTopPerformers(performers);
        setVendors(performers);
        setTotalVendors(performers.length);
      }
    } catch (error) {
      console.error('Error fetching top performers:', error);
      setTopPerformers([]);
      setVendors([]);
      setTotalVendors(0);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPage(0);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleVendorClick = (vendor: any) => {
    setSelectedVendor(vendor);
    setDetailsOpen(true);
  };

  const handleApproveVendor = async (vendorId: string) => {
    try {
      // Mock API call
      console.log('Approving vendor:', vendorId);
      fetchVendorData(); // Refresh data
    } catch (error) {
      console.error('Error approving vendor:', error);
    }
  };

  const renderDashboard = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                <VendorIcon />
              </Avatar>
              <Typography variant="h6">Total Vendors</Typography>
            </Box>
            <Typography variant="h4" color="primary">
              {vendorStats.totalVendors}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Registered vendors
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                <ActiveIcon />
              </Avatar>
              <Typography variant="h6">Active Vendors</Typography>
            </Box>
            <Typography variant="h4" color="success.main">
              {vendorStats.activeVendors}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Currently active
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                <PendingIcon />
              </Avatar>
              <Typography variant="h6">Pending Approval</Typography>
            </Box>
            <Typography variant="h4" color="warning.main">
              {vendorStats.pendingApproval}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Need review
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                <SpendingIcon />
              </Avatar>
              <Typography variant="h6">Total Spent</Typography>
            </Box>
            <Typography variant="h4" color="info.main">
              ${vendorStats.totalSpent?.toLocaleString() || '0'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This year
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Top Performing Vendors
          </Typography>
          <List>
            {vendorStats.topPerformers?.map((vendor: any, index: number) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <Badge badgeContent={index + 1} color="primary">
                    <PerformanceIcon />
                  </Badge>
                </ListItemIcon>
                <ListItemText
                  primary={vendor.name}
                  secondary={`Rating: ${vendor.rating || 0}/5 • Spent: $${vendor.spent?.toLocaleString() || '0'}`}
                />
                <ListItemSecondaryAction>
                  <Rating value={vendor.rating} readOnly size="small" />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Activity
          </Typography>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 32, height: 32 }}>
                <CheckCircle />
              </Avatar>
              <Box>
                <Typography variant="body2">
                  ABC Maintenance approved for emergency services
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  2 hours ago
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'warning.main', mr: 2, width: 32, height: 32 }}>
                <WarningIcon />
              </Avatar>
              <Box>
                <Typography variant="body2">
                  Insurance certificate expires in 30 days
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Pro Cleaning Services
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'info.main', mr: 2, width: 32, height: 32 }}>
                <AddIcon />
              </Avatar>
              <Box>
                <Typography variant="body2">
                  New vendor application received
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  New Electric Solutions
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderVendorTable = () => (
    <Paper>
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search vendors..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending_approval">Pending</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="maintenance_repair">Maintenance</MenuItem>
                <MenuItem value="utilities">Utilities</MenuItem>
                <MenuItem value="cleaning_supplies">Cleaning</MenuItem>
                <MenuItem value="professional_services">Services</MenuItem>
                <MenuItem value="emergency">Emergency</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" startIcon={<AddIcon />}>
                Add Vendor
              </Button>
              <Button variant="outlined" startIcon={<RefreshIcon />}>
                Refresh
              </Button>
              <Button variant="outlined" startIcon={<ExportIcon />}>
                Export
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : (
              vendors?.map((vendor: any) => (
                <TableRow 
                  key={vendor._id}
                  hover
                  onClick={() => handleVendorClick(vendor)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                        <VendorIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2">
                          {vendor.companyName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {vendor.vendorId}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {vendor.primaryContact?.firstName} {vendor.primaryContact?.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {vendor.primaryContact?.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <CategoryChip 
                      size="small" 
                      category={vendor.category}
                      label={vendor.category.replace('_', ' ')} 
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {vendor.address?.city}, {vendor.address?.state}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Rating 
                        value={vendor.performance?.rating?.overall || 0} 
                        readOnly 
                        size="small" 
                      />
                      <Typography variant="caption" sx={{ ml: 1 }}>
                        ({vendor.performance?.rating?.overall || 0})
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <StatusChip 
                      size="small" 
                      status={vendor.status}
                      label={vendor.status.replace('_', ' ')} 
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {vendor.status === 'pending_approval' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApproveVendor(vendor._id);
                              }}
                            >
                              <ApprovalIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton size="small">
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Edit">
                        <IconButton size="small">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalVendors}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Vendor Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage suppliers, contractors, and service providers
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label="Dashboard" />
          <Tab label="All Vendors" />
          <Tab label="Pending Approval" />
          <Tab label="Performance" />
        </Tabs>
      </Paper>

      <TabPanel value={currentTab} index={0}>
        {renderDashboard()}
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {renderVendorTable()}
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {renderVendorTable()}
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Performance analytics and vendor comparison tools coming soon!
        </Alert>
      </TabPanel>

      {/* Vendor Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Vendor Details
        </DialogTitle>
        <DialogContent>
          {selectedVendor && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Company Information</Typography>
                <Typography><strong>Name:</strong> {selectedVendor.companyName}</Typography>
                <Typography><strong>Type:</strong> {selectedVendor.vendorType}</Typography>
                <Typography><strong>Category:</strong> {selectedVendor.category}</Typography>
                <Typography><strong>Status:</strong> {selectedVendor.status}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Contact Information</Typography>
                <Typography><strong>Contact:</strong> {selectedVendor.primaryContact?.firstName} {selectedVendor.primaryContact?.lastName}</Typography>
                <Typography><strong>Email:</strong> {selectedVendor.primaryContact?.email}</Typography>
                <Typography><strong>Phone:</strong> {selectedVendor.primaryContact?.phone}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={<EditIcon />}>Edit</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}